/**
 * The versioning client — every request a versions view makes, in one
 * testable module, keyed by ISLAND id (any versioned island root, not only
 * projects). Reads and row-shaped verbs ride the generated API (registered
 * queries + command rows: history, branches, working/commit ops, commit);
 * the computed answers and engine transactions (diff, undo/rollback,
 * fork/checkout/discard, merge/baseline, as-of, names) are the surviving
 * custom routes, wrapped here so no page holds request code. Every call
 * carries the session cookie and the bound branch header.
 */
import { assertWritesAllowed } from "@sourcepilot/framework/write-gate";
import { VersioningBranch } from "@sourcepilot/generated/shared/api/versioningbranch";
import { VersioningCommit } from "@sourcepilot/generated/shared/api/versioningcommit";
import { VersioningOverlay } from "@sourcepilot/generated/shared/api/versioningoverlay";
import { VersioningOp } from "@sourcepilot/generated/shared/api/versioningop";
import { QueryNames } from "@sourcepilot/generated/query-names";
import type { VersioningBranchReadAPI } from "@sourcepilot/generated/shared/types/versioningbranch";
import type { VersioningCommitReadAPI } from "@sourcepilot/generated/shared/types/versioningcommit";
import { API_BASE_URL, apiFetch } from "./hooks/use-neo-client";
import { ApiError } from "./api-error";
import type { LaterChange, RevertPreview } from "../ui/organisms/versions/revert-preview";
import { branchHeaders } from "./active-branch";
import { readHolderHeaders } from "./active-holder";
import { pendingChangesChanged, sharedPendingChanges } from "./pending-changes-cache";
import { fellSilent, lastCommitProgress, stillReporting } from "./commit-progress";
import { forgetNotedConflict, NOTED_WIRE, notedIn, noteWrite, type NotedConflict } from "./pins/noted-conflicts";

// ---- shapes ---------------------------------------------------------------

export type Commit = VersioningCommitReadAPI & { id: string };
export type Branch = VersioningBranchReadAPI & { branch: string };

export type FieldChange = { before: unknown; after: unknown };
export type EntityChange = {
  entity_type: string;
  op: "CREATE" | "MODIFY" | "DELETE";
  op_ids?: string[];
  fields?: Record<string, FieldChange>;
  after?: Record<string, unknown>;
  before?: Record<string, unknown>;
};
/** The diff route's computed grouping of the working line, per entity. */
export type WorkingDiff = {
  /** Authorised projection replaces all previously cached review content. */
  /** `hidden_count`: whole authored ops left out (the unit of `raw_op_count`);
   *  `reason`: the server's fixed text, which names nothing hidden (Authz
   *  98275a457) — shown as it is */
  visibility?: { policy: "replace"; has_hidden_changes: boolean; hidden_count?: number; reason?: string };
  /** W6 v4: the ops that still change something (what the panel counts and commits). */
  op_count: number;
  /** Every uncommitted op in the log, netted or not. */
  raw_op_count?: number;
  /** Ops whose effect netted away (an undone op and its inverse): hidden from the cards. */
  netted_op_ids?: string[];
  diff: {
    entities: Record<string, EntityChange>;
    edges: {
      added: [string, string, string][];
      removed: [string, string, string][];
      moved: [string, string, string, string][];
      prop_set: [string, string, string, unknown][];
      /** W6: per edge, {property: [first before, last after]} of what the window still changes. */
      prop_changed?: [string, string, string, Record<string, [unknown, unknown]>][];
      /** per pin id, the net links added or given the pin in the window
       *  (diff.py "PINNED LINKS by pin"): the links an IslandPin change is
       *  shown with */
      pinned?: Record<string, [string, string, string][]>;
    };
  };
  collisions?: unknown[];
  /** A COMMIT's undo selection (changes.json, Authz A5): the authored ops this
   *  reader may see, relationship-only and netted ones included — never the
   *  cards'. `complete` is false when any op touches hidden content (its id is
   *  then left out), and a whole-commit undo is offered only when it is true. */
  selection?: { op_ids: string[]; complete: boolean };
  /** Per edge card, the authored op ids behind it, index-aligned with
   *  `diff.edges.<same key>` (Authz, 330de04b8); entity cards carry their
   *  own `op_ids`. Visible ops only: a card whose op was hidden does not
   *  exist. An op is a whole write event, so undoing one card's ops can
   *  reverse another card of the same op — the undo preview says which. */
  edge_op_ids?: { added?: string[][]; removed?: string[][]; moved?: string[][]; prop_set?: string[][] };
  /** what this read showed — the base, the lane's gen, its own ops — sent
   *  back on commit / accept, which are refused `changed_since_review` if any
   *  of it moved since (VC-core 2026-10-12: never seal what the reviewer did
   *  not see) */
  review_token?: string | null;
};
export type AsOfGraph = {
  nodes: Record<string, Record<string, unknown> & { _type?: string }>;
  relationships: { source: string; rel_type: string; target: string }[];
};
export type DeltaLink = {
  rel_type?: string; target_id?: string; target_type?: string;
  anchor_side?: string; props?: Record<string, unknown>;
  /** a MOVE (versioning_design.md §7): the record's to-one slot re-pointed
   *  from `before` to `target_id`; only on `links_moved` entries */
  before?: string | null;
  before_type?: string | null; props_before?: Record<string, unknown>;
};
export type OpDelta = {
  created?: boolean; deleted?: boolean;
  fields?: Record<string, [unknown, unknown]>;
  links_added?: DeltaLink[]; links_removed?: DeltaLink[];
  links_updated?: DeltaLink[]; links_moved?: DeltaLink[];
};
/** One op as the engine serialises it (`Op.to_dict()`), read from a
 *  `VersioningOp` row's `data` with the row's stamped `commit_id`. */
export type TimelineOp = {
  op_id: string; kind: string; entity_type: string; entity_id: string;
  field?: string | null; rel_type?: string | null; target_id?: string | null;
  anchor_side?: string | null;
  before?: unknown; after?: unknown;
  /** the person's public user id (null when not readable) — never a name */
  actor: string | null;
  /** who made it, as the reader may see them (the op row's `actor_person`, VC-Authz) */
  actor_person?: ProposalPerson | null;
  changeset_id?: string | null; deps?: string[]; delta?: OpDelta | null;
  commit_id?: string | null;
};
export type Conflict = {
  kind: string;
  entity_id: string;
  detail?: string;
  ours?: unknown;
  theirs?: unknown;
  /** "revert": a reverted change that no longer fits (versioning_oplog.md §7)
   *  — settled by keep / take (`resolveRevertConflicts`), not by a baseline */
  source?: string;
  op_id?: string;
  /** what "take" deletes first: the record holding the slot and its
   *  ownership cascade (a derived value takes its expression with it) */
  take_deletes?: { id: string; class: string; name?: string | null }[];
  /** false: the change names a record you cannot see (shown as
   *  `{ hidden: true }`) — only "keep" can settle it */
  take?: boolean;
  /** a REVERT conflict's later change: who made it and in which commit
   *  (null = uncommitted, in the draft); ops carry no time of their own */
  later?: LaterChange;
};
/** One settlement of a conflict, or of a mark, reported by a baseline. */
export type LaneChoice = { kind: string; entity_id: string; detail?: string; choose: "mine" | "theirs" };
/** One settlement of a REVERT conflict: keep what is there now, or take the
 *  reverted state. */
export type RevertChoice = { kind: string; entity_id: string; detail?: string; choose: "keep" | "take" };
export const isRevertConflict = (c: Conflict): boolean => c.source === "revert";
/** MY OVERLAY on an island, as the surface shows it (plan.md Phase F). An
 *  overlay is a typed record (`VersioningOverlay`) and THE RECORD IS THE
 *  STATUS — what it sits on, how far behind it is, which of my changes now
 *  override a change made since (`marks`) and which can no longer be applied
 *  (`conflicts`, which block Commit). `pending` is the one thing that is not
 *  on the record: the count of my uncommitted ops. */
export type OverlayStatus = {
  overlay: string | null;
  exists: boolean;
  /** the record's id — what DISCARD deletes */
  id?: string;
  branch?: string;
  /** what Commit sends back as "the base I was looking at" */
  base_commit_id?: string | null;
  pending: number;
  new_commits: number;
  marks: Conflict[];
  conflicts: Conflict[];
};
/** What a baseline did. */
export type BaselineResult = {
  baselined: boolean;
  new_commits: number;
  ops: number;
  overrides: Conflict[];
  marks: Conflict[];
  conflicts: Conflict[];
  dropped: [string, string][];
  rewritten: [string, string][];
};
/** A Baseline (or a proposal's Accept) the engine stopped short of — nothing
 *  moved. `conflicts`: settle these and send them back as `choices`. */
export type LaneBlock = {
  reason?: string;
  baselined?: boolean;
  new_commits?: number;
  overrides?: Conflict[];
  conflicts?: Conflict[];
};
/** The merge/baseline 409 payload — one of three shapes (conflicts,
 *  constraint violations, dirty working lines); all render, none is
 *  swallowed. */
export type MergeBlock = {
  reason?: string;
  conflicts?: Conflict[];
  constraint_violations?: unknown[];
  dirty?: unknown;
};
export type PinStatus = {
  holder_id: string;
  entity_id?: string;
  commit_id?: string | null;
  content_hash?: string | null;
  fresh?: boolean;
  identity?: boolean;
  historical?: boolean;
  rebindable?: boolean;
};

// ---- errors ---------------------------------------------------------------

/** THE ONE REFUSAL (VC-D's refusal set 1, 2026-10-14): every refused
 *  versioning request answers `detail = {code, reason, failed[], next[],
 *  …extras}` — read into the one notice by `toRefusal`. Its message is the
 *  server's `reason`. Two kinds are their own subclasses because a page
 *  ACTS on their extras rather than only showing them: a merge's block
 *  (`MergeConflict`) and an overlay's conflicts (`LaneBlocked`). */
export class VersioningError extends Error {
  constructor(public readonly status: number, public readonly detail: unknown, message?: string) {
    super(message ?? `versioning request failed (${status})`);
    this.name = "VersioningError";
  }
  /** the refusal's code (`nothing_to_commit`, `busy_try_again`, …), when it has one */
  get code(): string | undefined {
    return refusalCode(this.detail);
  }
}

/** A refusal body's code: its own, else the one its extras name (VC-D's
 *  `_KEY_CODES`, for a body sent before the route moved to the shape). */
export function refusalCode(detail: unknown): string | undefined {
  if (!detail || typeof detail !== "object") return undefined;
  const d = detail as Record<string, unknown>;
  if (typeof d.code === "string" && d.code) return d.code;
  if (d.conflicts) return "conflicts";
  if (d.rebased) return "base_moved";
  if (d.new_commits || d.moved) return "branch_moved";
  if (d.deferred) return "busy_try_again";
  if (d.dirty) return "merge_dirty";
  if (d.constraint_violations) return "constraint_violations";
  return undefined;
}

/** the merge refusals whose block the merge view renders */
const MERGE_BLOCKS = new Set(["conflicts", "constraint_violations", "merge_dirty"]);

/** A merge or baseline the engine refused with a structured block. */
export class MergeConflict extends VersioningError {
  constructor(public readonly block: MergeBlock) {
    super(409, block, "merge blocked");
    this.name = "MergeConflict";
  }
}

/** Commit or Baseline of an OVERLAY stopped short: see `LaneBlock`. */
export class LaneBlocked extends VersioningError {
  constructor(public readonly block: LaneBlock) {
    super(409, block, block.reason ?? "stopped short");
    this.name = "LaneBlocked";
  }
}

/** An overlay Commit REFUSED because a background re-base marked changes of
 *  mine I had not seen: nothing was sealed. `overrides` are the changes made
 *  on the branch since I started that mine now override; committing again
 *  with `base_commit_id` as the expected base seals, and is my
 *  acknowledgement that I saw them. */
export type RebasedRefusal = {
  reason?: string;
  overrides: Conflict[];
  base_commit_id?: string | null;
};

/** The re-base refusal a failed commit carries, or null for any other error.
 *  The commit is a create through the generated API, so the refusal arrives
 *  as an `ApiError` (409) whose detail has `rebased: true`. */
export function rebasedRefusal(e: unknown): RebasedRefusal | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const d = e.detail as Partial<RebasedRefusal> & { rebased?: unknown } | null;
  if (!d || typeof d !== "object" || d.rebased !== true) return null;
  return {
    reason: typeof d.reason === "string" ? d.reason : undefined,
    overrides: Array.isArray(d.overrides) ? d.overrides : [],
    base_commit_id: d.base_commit_id ?? null,
  };
}

/** A selection of uncommitted ops with its prerequisites (the selection
 *  route's answer, and the shape of a refused confirmed set's `detail`). */
export interface Selection {
  branch?: string;
  selected: string[];
  pulled_in: { op_id: string; entity_id: string; entity_type: string; kind: string; because: string[] }[];
  ops: TimelineOp[];
}

/** Someone on a proposal: who made it, who decided it. Set 23 (VC-D) sends
 *  `{identifier, name}`; today's rows hold the identifier alone. */
export type ProposalPerson = {
  id?: string; name?: string | null; avatar?: string | null; is_self?: boolean; hidden?: boolean;
  /** an ENGINE actor, not a person (VC-Authz 062e5e126): `{system: true,
   *  kind, name}` — e.g. a turn's commit, an agent's proposal */
  system?: boolean;
  kind?: "system" | "agent";
};

/** The flag is the contract (VC-Authz): never the "system:" prefix of the key. */
export const isEngineActor = (p: ProposalPerson | null | undefined): boolean => p?.system === true;

/** An engine actor in words: "System (turn)", "Agent (RFLPTask)". */
export function engineWords(p: ProposalPerson): string {
  const kind = p.kind === "agent" ? "Agent" : "System";
  return p.name ? `${kind} (${p.name})` : kind;
}

/** A presented person in words: their name, "you", or "someone" (hidden, or
 *  not presented) — never an id or a sign-in identifier. An engine actor is
 *  named as one ("System (turn)"), never as someone. */
export function personWords(p: ProposalPerson | null | undefined): string {
  if (p && isEngineActor(p)) return engineWords(p);
  if (!p || p.hidden) return "someone";
  return p.name || (p.is_self ? "you" : "someone");
}

/** A commit row as served: its author is a public id, beside `author_person`. */
export function commitAuthor(c: unknown): ProposalPerson | null {
  return ((c as { author_person?: ProposalPerson | null } | null)?.author_person) ?? null;
}

export type ProposalStatus = "open" | "accepted" | "rejected" | "withdrawn" | "discarded";

/** A CHANGE PROPOSAL: a branch with a target, reviewed and decided there. */
/** The chip's one read: the lane, its NET count, and (vc-impl, when sent)
 *  how many proposals are open on the island for the reader to review. */
export type PendingChanges = {
  branch: string; count: number; open_proposals?: number; holds_pins?: boolean;
  /** may the reader write the lane this answer names (main for a
   *  commit-on-save island; vc-impl): absent means unknown */
  can_write?: boolean;
  /** what the lane should resolve before it is committed or proposed; it
   *  never blocks either (vc-impl 783981aa3, ruling D) */
  to_resolve?: ToResolve[];
};
export type ToResolve = { kind: "retired_pin"; pin: string; island: string; type?: string; name?: string } | { kind: string; [k: string]: unknown };

export type Proposal = {
  /** the record's id */
  recordId: string;
  /** the proposal's own id: its lane is `proposal/<id>` */
  id: string;
  branch: string;
  target: string;
  author: ProposalPerson | null;
  isAuthor?: boolean;
  summary: string;
  status: ProposalStatus;
  /** set 23: who decided it, when, and why (absent until then) */
  decidedBy?: ProposalPerson | null;
  decidedAt?: string | null;
  decisionReason?: string | null;
  /** an accepted proposal's commit on its target */
  landedCommitId?: string | null;
  createdAt?: string | null;
  /** may the reader accept or reject it (vc-impl, when sent): false greys
   *  the decision out; absent leaves it to the server's answer */
  canReview?: boolean;
};

const person = (v: unknown): ProposalPerson | null => {
  if (!v || typeof v !== "object") return null;
  const p = v as Record<string, unknown>;
  if (p.hidden === true) return { hidden: true };
  if (p.system === true) return { system: true, kind: p.kind === "agent" ? "agent" : "system", name: typeof p.name === "string" ? p.name : null };
  return typeof p.id === "string" ? { id: p.id, name: typeof p.name === "string" ? p.name : null,
    avatar: typeof p.avatar === "string" ? p.avatar : null, is_self: p.is_self === true } : null;
};

/** A time from a row: epoch ms (set 23's proposals rows) or an ISO string,
 *  as an ISO string; null when absent or unreadable. */
const isoTime = (v: unknown): string | null => {
  if (typeof v === "number" && Number.isFinite(v)) return new Date(v).toISOString();
  if (typeof v === "string" && v) return v;
  return null;
};

/** A proposal row (the generated `proposals` query) as the UI uses it. */
export function proposalFromRow(r: Record<string, any>): Proposal | null {
  const branch = typeof r?.branch === "string" ? r.branch : "";
  if (!branch.startsWith("proposal/") || !r.proposal_target) return null;
  return {
    recordId: String(r.id ?? ""),
    id: branch.slice("proposal/".length),
    branch,
    target: String(r.proposal_target),
    // /api/query presents the author as a public id beside
    // `proposal_author_person` (VC-Authz); a proposals answer sends the person
    author: person(r.proposal_author_person ?? r.proposal_author),
    isAuthor: r.is_author === true || person(r.proposal_author_person ?? r.proposal_author)?.is_self === true,
    summary: String(r.proposal_summary ?? r.summary ?? ""),
    status: (r.proposal_status ?? "open") as ProposalStatus,
    decidedBy: person(r.decided_by),
    decidedAt: isoTime(r.decided_at),
    decisionReason: r.decision_reason ?? null,
    landedCommitId: r.landed_commit_id ?? null,
    createdAt: isoTime(r.created_at),
    ...(typeof r.can_review === "boolean" ? { canReview: r.can_review } : {}),
  };
}

/** A lane with no commits: `wip/<user>[//<branch>]` or `proposal/<id>`. */
export const isOverlay = (lane: string | null | undefined): boolean => /^(wip|proposal)\//.test(lane ?? "");

/** A lane as words: a named branch is its name; a DRAFT is never shown by
 *  its lane name, which carries its owner's user id (`wip/<user id>`) —
 *  it is "<person>'s draft" from the people
 *  data where there is a person, "your draft" for mine, else "a draft"; a
 *  proposal's lane is "a proposal". */
export function laneLabel(lane: string, owner?: ProposalPerson | null): string {
  if (lane.startsWith("wip/")) {
    if (owner && isEngineActor(owner)) return `${engineWords(owner)}'s draft`;
    if (owner && !owner.hidden) return owner.is_self ? "your draft" : owner.name ? `${owner.name}'s draft` : "a draft";
    return "a draft";
  }
  if (lane.startsWith("proposal/")) return "a proposal";
  return lane;
}


export type ReferencePin = {
  /** `redundant`: the references under this override agree again and
   *  removing it changes nothing, so the reader is offered its removal
   *  (Tom's V29, "One-click suggestion": never removed automatically);
   *  `review_required`: they agree, but removing it would change content */
  override_cleanup?: {state: string; candidate?: string; reason?: string} | null;
  /** the pin is only an override: no link of the holder's uses it (vc-impl
   *  7cb751153e), so it may be removed in one action when redundant */
  override_only?: boolean;
  /** V33: a route conflict kept on purpose ("Keep as override");
   *  `settled`: deliberate, or an override only */
  deliberate?: boolean;
  settled?: boolean;
  pin_id: string; island_id: string; mode: "pin" | "tracking"; watch_branch: string;
  selected: string | null; committed: string | null; available: string | null;
  pending_update: boolean; update_available: boolean; review_token: string;
  /** its target was retired (vc-impl 783981aa3, ruling D): a pin-level
   *  badge, the pin still reads its version; retired pins are listed first */
  retired?: boolean;
  /** V17 (vc-impl a787ad3c1): how many of the holder's links use the pin */
  link_count?: number;
  /** V17: each record the holder links to whose own content changed at the
   *  head of the watched branch, or that was removed there; up-to-date ones
   *  are left out ([] when nothing moved) */
  items?: ReferencePinItem[];
  /** vc-impl 6bd8a95c8: the target's root as this pin builds on it — its
   *  name AT `selected` (never today's) and its class. A readable pin only;
   *  absent or null when the version has none (then `/names` names it). */
  name?: string | null;
  type?: string | null;
};
export type ReferencePinItem = { id: string; type: string; state: "changed" | "removed" };
/** One linked record in an update preview (V17): its field changes (empty
 *  when it changed only through what it owns — its state still "changed"). */
export type ReferencePinPreviewItem = ReferencePinItem & {
  name?: string | null;
  changes?: Record<string, { before: unknown; after: unknown }>;
};
/** A pin the reader cannot read (vc-impl b1d311de3): one entry in the list,
 *  with the server's explanation and nothing else — no pin id, mode,
 *  version, token or action. `key` is opaque and per answer (hidden-1,
 *  hidden-2…): a render key only, never an identity. */
export type HiddenReferencePin = { key: string; hidden: true; island_id: null; explanation?: string | null };
/** A pin whose target was deleted (VC-D R5): listed after the hidden ones,
 *  `removed-1`, `removed-2`…, with no id, type or name; its preview, apply,
 *  mode and the draft's commit answer 409 `pin_removed`, whose fix is
 *  `removeRemovedReferences`. `key` is a render key only. */
export type RemovedReferencePin = { key: string; removed: true; island_id: null; explanation?: string | null };
export type ReferencePinEntry = ReferencePin | HiddenReferencePin | RemovedReferencePin;
/** What removing the references to removed items did (VC-D R5): how many
 *  pins and how many of the draft's links into them went. */
/** A redundant override taken out of the draft (or, on a commit-on-save
 *  island, committed). The pin is gone, so no new token: read the list again. */
export type RemovedOverrideResult = { staged?: boolean; committed?: boolean; branch?: string; pin_id?: string; removed_override?: boolean };
export type RemovedReferencesResult = { removed: number; links_removed?: number; staged?: boolean; committed?: boolean; branch?: string };
/** True for a readable pin: neither hidden nor removed. */
export function isReadablePin(p: ReferencePinEntry): p is ReferencePin {
  const x = p as { hidden?: unknown; removed?: unknown };
  return x.hidden !== true && x.removed !== true;
}
/** A record's name and type as the reader may see them now (`/names`);
 *  `description`: what a nameless record says, when the server sends it */
export type EntityLabel = { name?: string; type?: string; description?: string };
/** What a pin write (mode, update) answers, read from one snapshot after it
 *  committed (P2): the draft's NEW review token — every pin of the draft
 *  carries it — and the written pin as a list read shows it. */
export type ReferencePinWrite = { review_token?: string; pin?: ReferencePin };
/** A preview of a pin not made yet (pin it here): no pin id, nothing missing to count. */
export type NewReferencePinPreview = Omit<ReferencePinPreview, "pin_id" | "missing_reference_count"> & { island_id: string };
/** A record of the pinned item that is gone at the update's version, and
 *  this project's own records that link to it (VC-D, Tom's V19): accepting
 *  the update removes those links, as the user's own change. */
export type PinRemoval = {
  record: { id: string; type?: string | null; name?: string | null };
  linked_from: { id: string; type?: string | null; name?: string | null }[];
};
/** A ROUTE CONFLICT this change would create (V33, VC-D's contract): after
 *  it, this project would hold or reach an island at `pinned` while other
 *  routes reach it at `routes`' versions. `reason` is the server's sentence,
 *  hidden-island rules applied. `key` names the conflict in `resolutions`:
 *  the island id when it is readable, else an opaque key ("hidden-1"…)
 *  valid only with the preview token it came with. A hidden conflict has no
 *  `island`, `name` or `align`, and its routes show hidden islands as
 *  `{hidden: true}`. `align`: the one version every other route agrees on —
 *  previewing again with it makes the change agree — or null when they
 *  disagree among themselves (then only "keep as override" is offered). */
export type RouteConflict = {
  key: string;
  island?: string;
  name?: string | null;
  hidden?: boolean;
  pinned?: string | null;
  /** empty for a hidden conflict (its commits are withheld too) */
  routes: { commit: string; name?: string | null; via: (string | { hidden: true })[][] }[];
  /** `pin_id`: this project's pin of that island (the pins page aligns it) */
  align?: { island: string; commit: string; pin_id?: string } | null;
  reason: string;
};
/** island → commit: the alignments a preview was asked for. */
export type PinAlign = Record<string, string>;
/** conflict `key` → "override": each conflict kept, recorded as deliberate. */
export type PinResolutions = Record<string, "override">;

/** Another pin of this project an aligned update moves too (V33): each
 *  reviewed as its own update, inside the one combined change. Always
 *  readable (align is offered only for a readable conflict): `name` and
 *  `candidate_name` are its root's name at the old and the new version. */
export type AlignedPin = {
  pin_id: string; island?: string; name?: string | null; candidate_name?: string | null;
  selected: string | null; candidate: string;
  items?: ReferencePinPreviewItem[]; removes?: PinRemoval[]; can_apply: boolean;
};

/** The confirm's V33 fields: one resolution per conflict the preview still
 *  names, and — an update only — the alignments it was previewed with,
 *  exactly (create and the picker take no `align`). */
function conflictChoice(preview: { alignedWith?: PinAlign }, resolutions?: PinResolutions) {
  return {
    ...(resolutions && Object.keys(resolutions).length ? { resolutions } : {}),
    ...(preview.alignedWith && Object.keys(preview.alignedWith).length ? { align: preview.alignedWith } : {}),
  };
}
/** V33 noted conflicts: a write that left any says so in its answer; they
 *  join the holder's noted conflicts (the page alert), and the answer keeps
 *  them for the write's own warning */
const noting = <T>(holder: string, write: Promise<T>): Promise<T> => write.then((answer) => { noteWrite(holder, answer); return answer; });
const onlyResolutions = (resolutions?: PinResolutions) => (resolutions && Object.keys(resolutions).length ? { resolutions } : {});
const withAlign = (align?: PinAlign) => (align && Object.keys(align).length ? { align } : {});
/** a preview answer, stamped with what it was asked for when it was aligned:
 *  the alignments and the commit requested (an align of the pin's own island
 *  answers that commit as `candidate`, not the one asked for) */
function stamped<T extends object>(answer: T, align?: PinAlign, requested?: string): T & { alignedWith?: PinAlign; requestedCommit?: string } {
  if (!align || !Object.keys(align).length) return answer;
  return { ...answer, alignedWith: align, ...(requested ? { requestedCommit: requested } : {}) };
}

export type ReferencePinPreview = {
  /** Server-resolved save policy: adopting this preview commits immediately. */
  lands_now?: boolean;
  /** V33: the route conflicts this change would create ([] for none) */
  route_conflicts?: RouteConflict[];
  /** an update's: the other pins its `align` moves, in the same change */
  aligned?: AlignedPin[];
  /** client-side: the alignments this preview was made with, and the commit
   *  it was asked for; the confirm repeats both, exactly as the preview was
   *  asked (Codex F2), so the server rebuilds the same review */
  alignedWith?: PinAlign;
  requestedCommit?: string;
  pin_id: string; selected: string | null; candidate: string; preview_token: string; review_token: string;
  /** `missing_reference_count`: with VC-D's tracking set, how many linked
   *  records are gone at the candidate (their links are in `removes`, and
   *  accepting removes them); only `can_apply` refuses */
  can_apply: boolean; missing_reference_count: number; changes: Record<string, number>;
  /** V19: the links accepting removes, the preview token covering them */
  removes?: PinRemoval[];
  /** V17: the linked records this update changes or removes, for the
   *  review's list (the comparison is the whole island, for detail) */
  items?: ReferencePinPreviewItem[];
  /** vc-impl 6bd8a95c8: the target root's name at `selected` and at the
   *  candidate, for the review's "kg → kilogram" */
  name?: string | null;
  candidate_name?: string | null;
  /** P2: the FULL net difference between the selected and the candidate
   *  graphs, across any number of commits (intermediate edits that were
   *  reverted drop out), in the review cards' shape; its `op_ids` are always
   *  empty — a net comparison has no op identities. Replaces any earlier
   *  preview state (`visibility.policy`). */
  comparison?: {
    from_commit: string | null;
    to_commit: string;
    diff: WorkingDiff["diff"];
    visibility: NonNullable<WorkingDiff["visibility"]>;
  };
};

// ---- transport ------------------------------------------------------------

async function request<T>(path: string, init: RequestInit = {}, namespace = "versioning"): Promise<T> {
  // a versioning write to an island changes what pending-changes says of it
  const writes = namespace === "versioning" && (init.method ?? "GET").toUpperCase() !== "GET";
  // a page the server has not confirmed (drawn from the store, offline)
  // sends no write (client caching stage 2)
  if ((init.method ?? "GET").toUpperCase() !== "GET") assertWritesAllowed();
  try {
    return await send<T>(path, init, namespace);
  } finally {
    if (writes) pendingChangesChanged(path.split(/[/?]/)[0]);
  }
}

/** A READ THAT CAME AT A BUSY MOMENT IS SENT AGAIN BY ITSELF (VC-Authz,
 *  2026-10-26): while an automatic pin advance reconciles into the draft,
 *  about 3 reads of a holder's reference pins in 4 answer 409
 *  busy_try_again. Only that refusal, only on those reads — the listing and
 *  a pin's exploration (a write is never sent twice), after each of these waits (±20%); then the last error
 *  stands and the page says it with its "Try again". */
export const BUSY_RETRY_MS = [250, 500, 1_000, 2_000];

async function busyRetry<T>(read: () => Promise<T>): Promise<T> {
  for (let i = 0; ; i++) {
    try {
      return await read();
    } catch (e) {
      if (i >= BUSY_RETRY_MS.length || !(e instanceof VersioningError) || e.status !== 409 || e.code !== "busy_try_again") throw e;
      const ms = BUSY_RETRY_MS[i] * (0.8 + Math.random() * 0.4);
      await new Promise((r) => setTimeout(r, ms));
    }
  }
}

async function send<T>(path: string, init: RequestInit, namespace: string): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/${namespace ? `${namespace}/` : ""}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...branchHeaders(),
      // a GET from a holder's page names it; a versioning write never does
      ...readHolderHeaders(path, init),
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  });
  let body: any = null;
  const text = await res.text();
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!res.ok) {
    const detail = body?.detail ?? body;
    const code = res.status === 409 ? refusalCode(detail) : undefined;
    // only the refusals a page acts on are their own kind: an overlay's
    // conflicts (the record lists them to settle) and a merge's block. Every
    // other refusal — nothing to commit, busy, decided, ended … — is the one
    // refusal, shown with the server's reason (before, every 409 with a
    // body became one of these two, and the panel swallowed a LaneBlocked)
    if (code === "conflicts" && (/\/baseline$/.test(path) || /\/proposals\/[^/]+\/accept$/.test(path))) {
      throw new LaneBlocked(detail as LaneBlock);
    }
    if (code && MERGE_BLOCKS.has(code) && /\/merge$/.test(path)) {
      throw new MergeConflict(detail as MergeBlock);
    }
    // the server's own words when it gave them (its `reason`, or a string
    // detail), else the generic line
    const reason = typeof detail === "string" ? detail : typeof detail?.reason === "string" ? detail.reason : "";
    throw new VersioningError(res.status, detail, reason || `${init.method ?? "GET"} ${path} failed (${res.status})`);
  }
  return body as T;
}

/** A `VersioningOp` row → the op the engine serialised, with the stamped commit id. */
export function opFromRow(row: { data?: string | null; commit_id?: string | null; actor_person?: ProposalPerson | null }): TimelineOp | null {
  if (!row.data) return null;
  try {
    const op = JSON.parse(row.data) as TimelineOp;
    op.commit_id = row.commit_id ?? op.commit_id ?? null;
    if (row.actor_person) op.actor_person = row.actor_person;
    return op;
  } catch {
    return null;
  }
}

/** the names route answers at most this many ids per request (it keeps the
 *  first 200): ask in batches, so an id past the cap is still asked — a
 *  caller reads an id left out of the answer as one it may not read */
export const NAMES_BATCH = 200;

async function entityLabels(islandId: string, ids: string[]): Promise<Record<string, EntityLabel>> {
  if (!ids.length) return {};
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += NAMES_BATCH) batches.push(ids.slice(i, i + NAMES_BATCH));
  const bodies = await Promise.all(
    batches.map((batch) =>
      request<{ names?: Record<string, { name?: unknown; type?: unknown; description?: unknown }> }>(
        `${islandId}/names?ids=${batch.map(encodeURIComponent).join(",")}`,
      ),
    ),
  );
  const out: Record<string, EntityLabel> = {};
  for (const [id, v] of bodies.flatMap((body) => Object.entries(body.names ?? {}))) {
    const name = typeof v?.name === "string" && v.name ? v.name : undefined;
    const type = typeof v?.type === "string" && v.type ? v.type : undefined;
    const description = typeof v?.description === "string" && v.description ? v.description : undefined;
    if (name || type) out[id] = { ...(name ? { name } : {}), ...(type ? { type } : {}), ...(description ? { description } : {}) };
  }
  return out;
}

// ---- the client -----------------------------------------------------------

/** No answer came, or a server error did: the commit may still have landed. */
export function isLostAnswer(e: unknown): boolean {
  return !(e instanceof ApiError) || e.status >= 500;
}

/** How long a lost answer is waited on while the commit still reports, and
 *  how often it is asked for meanwhile. */
export const LOST_ANSWER_WAIT_MS = 10 * 60_000;
export const LOST_ANSWER_POLL_MS = 2_000;

/** Whether a commit whose answer was lost was not known to land. */
export class CommitOutcomeUnknown extends Error {
  readonly detail = {
    code: "commit_outcome_unknown",
    reason: "The connection was lost while committing, so it is not known whether your commit landed. Look at the history before committing again.",
    failed: [],
    next: [],
  };
  constructor() {
    super("The connection was lost while committing.");
    this.name = "CommitOutcomeUnknown";
  }
}

/** What commit-by-progress answers for an attempt: my commit, the server's
 *  short-lived record that it did not land (`outcome` "refused" or "failed",
 *  with its `code`), or nothing known yet. */
type AttemptByProgress = { commit: Commit } | { ended: "refused" | "failed"; code?: string } | null;

function landedAs(progressId: string): string | null {
  const e = lastCommitProgress(progressId)?.event;
  return e?.step === "landed" && e.commit_id ? e.commit_id : null;
}

/** After a lost answer: my commit with `progressId`, asked for now and again
 *  while the commit still reports its steps; null only on the server's own
 *  record that the attempt was refused or failed. Unknown — never "nothing
 *  was committed" — when the check cannot be made, when no step was ever
 *  heard (the commit may be running where I cannot see it), once it fell
 *  silent without either, or after waiting LOST_ANSWER_WAIT_MS.
 *
 *  Silence never proves nothing was committed (Codex R2, 2026-10-26): the
 *  steps are published best effort, apart from the commit's transaction,
 *  so an open socket hearing nothing says only that the steps stopped
 *  arriving.
 *
 *  Silence is not a stopped commit while the frames could not reach this
 *  page (item 258): it counts from the later of the last frame, the
 *  socket's last connect and the lost answer, and not at all while the
 *  socket is away. A commit whose request was still open sends a frame
 *  every 3 s, so silence already SILENCE_MS long when the answer was lost
 *  says the frames were not arriving: then only a frame heard after it
 *  shows this page can hear again; until then it keeps asking. */
async function findCommitAfterLostAnswer(
  islandId: string,
  progressId: string,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<Commit | null> {
  const lostAt = Date.now();
  const until = lostAt + LOST_ANSWER_WAIT_MS;
  // silent already: the frames were not reaching this page
  const deaf = !!lastCommitProgress(progressId) && !stillReporting(progressId, lostAt);
  for (;;) {
    let found: AttemptByProgress;
    try {
      found = await versioningClient.attemptByProgress(islandId, progressId);
    } catch {
      found = null;
      if (!landedAs(progressId)) throw new CommitOutcomeUnknown();
    }
    if (found && "commit" in found) return found.commit;
    // the server's record that it was refused or failed: the original error stands
    if (found) return null;
    // the server's own word, on my own channel, once its transaction committed
    const landed = landedAs(progressId);
    if (landed) return { id: landed, island_id: islandId, progress_id: progressId } as Commit;
    if (!lastCommitProgress(progressId) || Date.now() > until) throw new CommitOutcomeUnknown();
    const hearing = !deaf || (lastCommitProgress(progressId)?.at ?? 0) > lostAt;
    if (hearing && fellSilent(progressId, lostAt)) throw new CommitOutcomeUnknown();
    await sleep(LOST_ANSWER_POLL_MS);
  }
}

export const versioningClient = {
  calculationRead: (requestId: string) =>
    request<any>(`calculation-reads/${encodeURIComponent(requestId)}`),
  calculationState: (holder: string, expression: string, lane: string) =>
    request<any>(`${encodeURIComponent(holder)}/calculations/${encodeURIComponent(expression)}?lane=${encodeURIComponent(lane)}`),
  /** Independent readable-reference counts; a pin can qualify for both. */
  referencePins(holder: string, branch: string): Promise<{ pins: ReferencePinEntry[]; updates_available?: number; retired_references?: number }> {
    return busyRetry(() => request(`${holder}/reference-pins?branch=${encodeURIComponent(branch)}`));
  },
  exploreReferencePin(holder: string, pin: string, branch: string): Promise<Omit<ReferencePinPreview, "preview_token"> & { update_available: boolean; watch_branch: string }> {
    return busyRetry(() => request(`${holder}/reference-pins/${encodeURIComponent(pin)}/explore?branch=${encodeURIComponent(branch)}`));
  },
  /** `align` (V33): preview the change made to agree with those routes */
  async previewReferencePin(holder: string, pin: string, branch: string, commit_id?: string, align?: PinAlign): Promise<ReferencePinPreview> {
    const answer = await request<ReferencePinPreview>(`${holder}/reference-pins/${encodeURIComponent(pin)}/preview`, { method: "POST", body: JSON.stringify({ branch, ...(commit_id ? { commit_id } : {}), ...withAlign(align) }) });
    return stamped(answer, align, commit_id);
  },
  /** V33 Align: `shown` previewed again with `align` added to its own. It
   *  is asked at the commit `shown` was asked for — an unaligned preview's
   *  `candidate` (also when it was asked with no commit_id: the server's
   *  default), an aligned one's `requestedCommit` — never an aligned
   *  candidate (Codex F2). */
  alignReferencePin(holder: string, pin: string, branch: string, shown: ReferencePinPreview, align: PinAlign): Promise<ReferencePinPreview> {
    const asked = shown.alignedWith ? (shown.requestedCommit ?? shown.candidate) : shown.candidate;
    return versioningClient.previewReferencePin(holder, pin, branch, asked, { ...shown.alignedWith, ...align });
  },
  /** an aligned preview is confirmed as it was asked: the requested commit
   *  and the same `align` (aligning the pin's own island answered another
   *  `candidate`, from which the server would offer nothing) */
  updateReferencePin(holder: string, pin: string, branch: string, preview: ReferencePinPreview, resolutions?: PinResolutions): Promise<ReferencePinWrite> {
    const commit_id = preview.alignedWith && preview.requestedCommit ? preview.requestedCommit : preview.candidate;
    return noting(holder, request(`${holder}/reference-pins/${encodeURIComponent(pin)}/update`, { method: "POST", body: JSON.stringify({ branch, commit_id, preview_token: preview.preview_token, ...conflictChoice(preview, resolutions) }) }));
  },
  setReferencePinMode(holder: string, pin: ReferencePin, branch: string, policy: "pin" | "tracking"): Promise<ReferencePinWrite> {
    return request(`${holder}/reference-pins/${encodeURIComponent(pin.pin_id)}/mode`, { method: "POST", body: JSON.stringify({ branch, policy, watch_branch: pin.watch_branch, review_token: pin.review_token }) });
  },
  /** PIN IT HERE (vc-impl): a preview of the holder's OWN pin of an island
   *  its references reach at different versions, at one of those versions
   *  (`commitId` is required: no head default). The comparison is from
   *  nothing (`from_commit: null`, records as CREATE). */
  previewNewReferencePin(holder: string, branch: string, islandId: string, commitId: string): Promise<NewReferencePinPreview> {
    return request(`${holder}/reference-pins/preview`, { method: "POST", body: JSON.stringify({ branch, island_id: islandId, commit_id: commitId }) });
  },
  /** Stage that pin in the draft with the preview's token; answers the pin
   *  and the draft's new review token, as an update does. */
  createReferencePin(holder: string, branch: string, islandId: string, preview: ReferencePinPreview, resolutions?: PinResolutions): Promise<ReferencePinWrite & { staged?: boolean; pin_id?: string }> {
    return noting(holder, request(`${holder}/reference-pins`, { method: "POST", body: JSON.stringify({ branch, island_id: islandId, commit_id: preview.candidate, preview_token: preview.preview_token, ...onlyResolutions(resolutions) }) }));
  },
  /** V29: take a redundant override out of the draft, with the pin's review
   *  token from the listing; a 409 changed_since_review says it is still
   *  needed or the draft moved */
  removeReferenceOverride(holder: string, pin: ReferencePin, branch: string): Promise<RemovedOverrideResult> {
    return request(`${holder}/reference-pins/${encodeURIComponent(pin.pin_id)}/remove-override`, { method: "POST", body: JSON.stringify({ branch, review_token: pin.review_token }) });
  },
  /** VC-D R5: drop every reference to a removed item from the draft, and
   *  the draft's links into it (they show in the draft's changes as the
   *  user's own); then the draft is committed as usual */
  removeRemovedReferences(holder: string, branch: string): Promise<RemovedReferencesResult> {
    return request(`${holder}/reference-pins/remove-removed`, { method: "POST", body: JSON.stringify({ branch }) });
  },
  commitChanges(islandId: string, commitId: string): Promise<WorkingDiff> {
    return request(`${islandId}/commits/${encodeURIComponent(commitId)}/changes.json`);
  },
  /** A branch's commit chain, newest first (registered query). */
  async history(islandId: string, branch = "main", opts: { limit?: number } = {}): Promise<Commit[]> {
    let q: any = VersioningCommit.setQuery(QueryNames.versioningCommitHistory).filter({ island_id: islandId, branch });
    if (opts.limit) q = q.limit(opts.limit);
    const rows = await q.all();
    return (rows as unknown as Commit[]).filter((c) => !!c.id);
  },

  /** Every branch head of the island (registered query). */
  pickerBrowse(holder: string, body: Record<string, unknown>): Promise<any> {
    return request(`${holder}/reference-picker/browse`, { method: "POST", body: JSON.stringify(body) });
  },
  /** answers `route_conflicts` at the top (a new pin or an existing one) */
  pickerPreview(holder: string, selection: Record<string, unknown>): Promise<any> {
    return request(`${holder}/reference-picker/preview`, { method: "POST", body: JSON.stringify(selection) });
  },
  pickerApply(holder: string, preview: { selection: Record<string, unknown>; preview_token: string }, resolutions?: PinResolutions): Promise<any> {
    return noting(holder, request(`${holder}/reference-picker/apply`, { method: "POST", body: JSON.stringify({ selection: preview.selection, preview_token: preview.preview_token, ...onlyResolutions(resolutions) }) }));
  },
  /** V33 noted conflicts: this project's pin of `island` in `branch`, as the
   *  references listing has it — the pin a conflict is settled through, and
   *  the review token keep sends (VC-D: the listing is its source). */
  async pinOfIsland(holder: string, branch: string, island: string): Promise<ReferencePin> {
    const pin = (await versioningClient.referencePins(holder, branch)).pins.filter(isReadablePin).find((p) => p.island_id === island);
    if (!pin) throw { status: 409, detail: { code: "changed_since_review", reason: "This project no longer pins that item here. Review its references again.", failed: [], next: [] } };
    return pin;
  },
  /** V33 noted conflicts: keep this project's pin in a noted conflict as a
   *  deliberate override (VC-D's `keep` route, with the listing's review
   *  token; Align is a pin update, previewed first). */
  async keepNotedConflict(holder: string, branch: string, conflict: NotedConflict & { island: string }): Promise<ReferencePinWrite> {
    const pin = await versioningClient.pinOfIsland(holder, branch, conflict.island);
    const answer = await request<ReferencePinWrite>(NOTED_WIRE.keepPath(holder, conflict.pin_id ?? pin.pin_id), { method: "POST", body: JSON.stringify({ branch, review_token: pin.review_token }) });
    forgetNotedConflict(holder, conflict);
    return answer;
  },

  async branches(islandId: string): Promise<Branch[]> {
    const rows = await VersioningBranch.setQuery(QueryNames.versioningBranches)
      .filter({ island_id: islandId })
      .all();
    return (rows as unknown as Branch[]).filter((b) => !!b.branch);
  },

  /** The working line's ops in order (registered query over the op rows). */
  async workingOps(islandId: string, branch = "main"): Promise<TimelineOp[]> {
    const rows = await VersioningOp.setQuery(QueryNames.versioningWorkingOps)
      .filter({ island_id: islandId, branch })
      .all();
    return (rows as unknown as Parameters<typeof opFromRow>[0][])
      .map(opFromRow)
      .filter((o): o is TimelineOp => o !== null);
  },

  /** One commit's ops in order (registered query over the op rows). */
  async commitOps(islandId: string, commitId: string): Promise<TimelineOp[]> {
    const rows = await VersioningOp.setQuery(QueryNames.versioningCommitOps)
      .filter({ island_id: islandId, commit_id: commitId })
      .all();
    return (rows as unknown as Parameters<typeof opFromRow>[0][])
      .map(opFromRow)
      .filter((o): o is TimelineOp => o !== null);
  },

  /** COMMIT the working line: a command row — creating it seals the commit. */
  /** COMMIT a lane's uncommitted changes: a command row — creating it seals
   *  the commit. The SAME request whether the lane is a branch or my overlay
   *  (an overlay differs only in who can see it); for an overlay the engine
   *  brings its changes onto its branch and the overlay ends. Pass
   *  `expectedBase` — the overlay's `base_commit_id` as I was shown it — so
   *  that a change re-written while the commit dialog was open is never
   *  sealed unseen: the create is refused and I look again. */
  async commit(
    islandId: string,
    branch: string,
    message: string,
    expectedBase?: string | null,
    opIds?: string[] | null,
    /** the review's `review_token` (see WorkingDiff) */
    reviewToken?: string | null,
    /** this attempt's id (a uuid4, `newProgressId`): the server reports the
     *  commit's steps under it, and a lost answer is checked by it */
    progressId?: string | null,
  ): Promise<Commit> {
    const row = new VersioningCommit({
      island_id: islandId,
      branch,
      message,
      ...(expectedBase ? { expected_base_commit_id: expectedBase } : {}),
      ...(reviewToken ? { review_token: reviewToken } : {}),
      ...(progressId ? { progress_id: progressId } : {}),
      // SELECTIVE COMMIT (oplog §6): exactly the ops the dialog showed and
      // the user confirmed — the whole set `selection()` answered with; a set
      // that is not closed any more is refused with the same shape
      ...(opIds && opIds.length ? { requested_op_ids: opIds } : {}),
    } as any);
    try {
      await row.save();
    } catch (e) {
      // A LOST ANSWER (no response, or a 5xx a proxy may give while the
      // commit goes on) does not mean nothing was committed: ask for my
      // commit with this attempt's id — while the commit is still reporting
      // its steps, until it lands or falls silent
      if (!progressId || !isLostAnswer(e)) throw e;
      const found = await findCommitAfterLostAnswer(islandId, progressId);
      if (found) return found;
      throw e;
    }
    return row as unknown as Commit;
  },

  /** My own commit made with this attempt's id, or null (the server matches
   *  only the caller's own commits). */
  async commitByProgress(islandId: string, progressId: string): Promise<Commit | null> {
    const found = await versioningClient.attemptByProgress(islandId, progressId);
    return found && "commit" in found ? found.commit : null;
  },

  /** My own commit made with this attempt's id, the server's record that the
   *  attempt was refused or failed, or null. */
  async attemptByProgress(islandId: string, progressId: string): Promise<AttemptByProgress> {
    // the attempt's `outcome` comes beside the rows, at the top of the body
    // (VC-housekeeping 1bdd6d41ba): keep the body the builder reads
    let body: { outcome?: unknown; code?: unknown; commit_id?: unknown } | null = null;
    const keepBody = async (path: string, options?: RequestInit) => (body = await apiFetch(path, options));
    const builder = VersioningCommit.setQuery(QueryNames.versioningCommitByProgress)
      .filter({ island_id: islandId, progress_id: progressId }) as unknown as {
        first(fetchFn: typeof keepBody): Promise<Commit | null>;
      };
    const row = await builder.first(keepBody);
    if (row?.id) return { commit: row };
    const { outcome, code, commit_id } = (body ?? {}) as NonNullable<typeof body>;
    if (outcome === "landed" && typeof commit_id === "string" && commit_id) {
      return { commit: { id: commit_id, island_id: islandId, progress_id: progressId } as Commit };
    }
    if (outcome === "refused" || outcome === "failed") {
      return { ended: outcome, ...(typeof code === "string" ? { code } : {}) };
    }
    return null; // "unknown": not ended yet, or not mine to know
  },

  /** SHOW before ASK: a selection of my uncommitted ops answered with what
   *  is pulled in because the selection depends on it, and the ops of the
   *  closed set in apply order — nothing committed. `commit(..., opIds)` with
   *  every op shown is the ASK. */
  selection(islandId: string, ids: string[], branch?: string): Promise<Selection> {
    const qs = new URLSearchParams({ ids: ids.join(",") });
    if (branch) qs.set("branch", branch);
    return request<Selection>(`${islandId}/working/selection?${qs.toString()}`);
  },

  /** Every registered pin into the island with its freshness against the
   * head (the staleness surface): keyed by holder id. */
  async pins(islandId: string): Promise<Record<string, PinStatus>> {
    const body = await request<{ pins?: PinStatus[] }>(`${islandId}/pins`);
    const byHolder: Record<string, PinStatus> = {};
    for (const p of body.pins ?? []) byHolder[p.holder_id] = p;
    return byHolder;
  },

  /** Count + branch of the uncommitted ops on the island's working lane
   * (the session's active lens when `branch` is omitted). */
  /** Shared: one request per island, lane and access, cleared when the
   *  island changes (pending-changes-cache). */
  pendingChanges(islandId: string, branch?: string): Promise<PendingChanges> {
    return sharedPendingChanges(islandId, branch, (b) =>
      request<PendingChanges>(`${islandId}/pending-changes${b ? `?branch=${encodeURIComponent(b)}` : ""}`));
  },

  /** The uncommitted working changeset, grouped per entity (computed). */
  diff(islandId: string, branch = "main"): Promise<WorkingDiff> {
    return request<WorkingDiff>(`${islandId}/diff?branch=${encodeURIComponent(branch)}`);
  },

  /** Names for ids the ops never carried (batched lookup). */
  async names(islandId: string, ids: string[]): Promise<Record<string, string>> {
    const out: Record<string, string> = {};
    for (const [id, v] of Object.entries(await entityLabels(islandId, ids))) if (v.name) out[id] = v.name;
    return out;
  },

  /** Name and type for ids, as the reader may see them now: one batched,
   *  secured read; an id the reader can't read is left out. */
  labels(islandId: string, ids: string[]): Promise<Record<string, EntityLabel>> {
    return entityLabels(islandId, ids);
  },

  /** What a revert of these ops would do, before anything is written: the
   *  selection grouped by commit, and each revert conflict with the later
   *  change it meets. Nothing later is pulled in (Tom, 2026-10-06). */
  /** `keep`: later changes the user keeps (their op ids), settling the
   *  conflicts they meet; the token then covers them, so the confirm sends
   *  the same `keep` (vc-impl C4 part 2) */
  async revertPreview(islandId: string, opIds: string[], keep: string[] = []): Promise<RevertPreview> {
    const body = await request<Partial<RevertPreview>>(`${islandId}/working/undo`, {
      method: "POST",
      body: JSON.stringify({ op_ids: opIds, confirm: false, keep }),
    });
    return {
      closure: body.closure ?? [],
      groups: body.groups ?? [],
      conflicts: body.conflicts ?? [],
      needs_confirmation: body.needs_confirmation,
      too_large: body.too_large,
      write_refusals: body.write_refusals ?? [],
      ...(typeof body.preview_token === "string" ? { preview_token: body.preview_token } : {}),
    };
  },

  /** Undo / revert: the inverses are APPENDED to my lane as ordinary
   *  uncommitted edits (W6) — nothing is removed; the changeset view nets
   *  an op and its inverse to nothing. */
  /** `previewToken`: the preview's (vc-impl C4); the confirm is refused
   *  without it once the server asks for it */
  undo(islandId: string, opIds: string[], previewToken?: string, keep: string[] = []): Promise<unknown> {
    return noting(islandId, request(`${islandId}/working/undo`, {
      method: "POST",
      body: JSON.stringify({ op_ids: opIds, confirm: true, keep, ...(previewToken ? { preview_token: previewToken } : {}) }),
    }));
  },

  rollback(islandId: string, toOpId: string): Promise<unknown> {
    return request(`${islandId}/working/rollback`, {
      method: "POST",
      body: JSON.stringify({ to_op_id: toOpId }),
    });
  },

  fork(islandId: string, name: string, atCommit?: string): Promise<{ branch: string; fork_commit_id: string }> {
    return request(`${islandId}/branches`, {
      method: "POST",
      body: JSON.stringify(atCommit ? { name, at_commit: atCommit } : { name }),
    });
  },

  checkout(islandId: string, branch: string): Promise<unknown> {
    return request(`${islandId}/branches/${encodeURIComponent(branch)}/checkout`, { method: "POST" });
  },

  discard(islandId: string, branch: string): Promise<unknown> {
    return request(`${islandId}/branches/${encodeURIComponent(branch)}/checkout`, { method: "DELETE" });
  },

  /** Clean-merge into main; a structured refusal throws `MergeConflict`. */
  merge(islandId: string, branch: string, message = `merge ${branch}`): Promise<unknown> {
    return request(`${islandId}/branches/${encodeURIComponent(branch)}/merge`, {
      method: "POST",
      body: JSON.stringify({ message }),
    });
  },

  /** BASELINE: move my overlay's base up to its branch's head. What is
   *  already true drops out, what still applies is re-written and MARKED,
   *  what cannot apply is a conflict — then nothing moves and this throws
   *  `LaneBlocked`; send the conflicts back settled in `choices`. */
  baseline(islandId: string, overlay?: string | null, choices: LaneChoice[] = []): Promise<BaselineResult> {
    return request(`${islandId}/baseline`, {
      method: "POST",
      body: JSON.stringify({ branch: overlay ?? null, choices }),
    });
  },

  /** Settle REVERT conflicts on my overlay: KEEP closes one and writes
   *  nothing; TAKE writes the reverted state (for a held slot: deletes
   *  `take_deletes`, then brings the record back) as ordinary uncommitted
   *  changes. Answers what was settled and what is still open. */
  resolveRevertConflicts(
    islandId: string,
    overlay: string | null | undefined,
    choices: RevertChoice[]
  ): Promise<{ resolved: number; open: Conflict[] }> {
    return request(`${islandId}/revert-conflicts`, {
      method: "POST",
      body: JSON.stringify({ branch: overlay ?? null, choices }),
    });
  },

  /** My overlay on this island: the RECORD (through the secured read — I
   *  only ever get overlays I may see) plus my pending count. */
  async overlayStatus(islandId: string): Promise<OverlayStatus> {
    const pending = await this.pendingChanges(islandId);
    const none: OverlayStatus = { overlay: null, exists: false, pending: 0, new_commits: 0, marks: [], conflicts: [] };
    if (!isOverlay(pending.branch)) return none;
    const rows = await VersioningOverlay.setQuery(QueryNames.versioningOverlays)
      .filter({ island_id: islandId, branch: pending.branch })
      .all();
    const rec = (rows as any[]).find((r) => r?.branch === pending.branch);
    if (!rec) return { ...none, overlay: pending.branch };
    const list = (v: unknown): Conflict[] => {
      try { return typeof v === "string" && v ? (JSON.parse(v) as Conflict[]) : []; } catch { return []; }
    };
    // REVERT conflicts have their own field, and are shown with what is
    // there NOW (read live by the server): main may have moved the fact
    // since the conflict was recorded
    const recorded = list(rec.revert_conflicts).map((c) => ({ ...c, source: "revert" }));
    const reverts = recorded.length
      ? await this.openRevertConflicts(islandId, pending.branch).catch(() => recorded)
      : [];
    return {
      overlay: pending.branch,
      exists: true,
      id: rec.id,
      branch: rec.base_branch ?? "main",
      base_commit_id: rec.base_commit_id ?? null,
      pending: pending.count,
      new_commits: rec.new_commits ?? 0,
      marks: list(rec.marks),
      conflicts: [...list(rec.conflicts), ...reverts],
    };
  },

  /** My overlay's open REVERT conflicts, each with what is there NOW. */
  async openRevertConflicts(islandId: string, overlay: string | null | undefined): Promise<Conflict[]> {
    const q = overlay ? `?branch=${encodeURIComponent(overlay)}` : "";
    const body = await request<{ open: Conflict[] }>(`${islandId}/revert-conflicts${q}`);
    return (body.open ?? []).map((c) => ({ ...c, source: "revert" }));
  },

  /** DISCARD my overlay: delete its record — my uncommitted changes are
   *  thrown away, the branch is untouched. */
  // ---- proposals ----------------------------------------------------------

  /** The island's change proposals (registered query; secured: only those
   *  the reader may see), newest first. */
  async proposals(islandId: string, status?: ProposalStatus): Promise<Proposal[]> {
    const rows = await VersioningOverlay.setQuery(QueryNames.versioningProposals)
      .filter({ island_id: islandId, ...(status ? { status } : {}) })
      .all();
    const out = (rows as any[]).map(proposalFromRow).filter((p): p is Proposal => !!p);
    // Detail is the A4 public-person projection. Generated overlay rows
    // still contain internal author keys and are never presentation data.
    // can_review is the LISTING's (vc-impl: one decision per row read; the
    // detail route never sends it): carried over onto the detail
    const detailed = await Promise.all(out.map(async (p) => {
      const d = proposalFromRow(await request<Record<string, unknown>>(`${islandId}/proposals/${encodeURIComponent(p.id)}`));
      return d && p.canReview !== undefined ? { ...d, canReview: p.canReview } : d;
    }));
    // newest first (set 23 orders on created_at; until then, by what is known)
    return detailed.filter((p): p is Proposal => !!p).sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? "")));
  },

  /** What a proposal lands — open or closed: its net changes, in the /diff
   *  shape, with its `status`, and the `review_token` an Accept sends while it
   *  is open (set 23's closed view, `changes.json`: a proposal keeps its ops
   *  when decided, so a decided one shows what it was). */
  proposalChanges(islandId: string, proposalId: string): Promise<WorkingDiff & { status?: ProposalStatus }> {
    return request(`${islandId}/proposals/${encodeURIComponent(proposalId)}/changes.json`);
  },

  /** PROPOSE FROM MY DRAFT (set 23): one transaction opens a proposal on
   *  `targetBranch` (default: the draft's own line), moves my draft's changes
   *  onto it, and leaves the draft empty at a new generation. `reviewToken`:
   *  what the review showed — refused `changed_since_review` if the draft
   *  moved since. Refusals come in the uniform body (`nothing_to_propose`,
   *  403 when I may not propose onto the target). */
  async proposeFromDraft(
    islandId: string,
    summary: string,
    opts: { reviewToken?: string | null; targetBranch?: string | null } = {},
  ): Promise<{ proposal: Proposal | null; proposalId: string; targetBranch: string; movedOps: number }> {
    const r = await request<{ proposal_id: string; branch: string; target_branch: string; moved_ops: number; draft_gen?: string; proposal?: Record<string, any> }>(
      `${islandId}/proposals/from-draft`,
      {
        method: "POST",
        body: JSON.stringify({
          summary,
          ...(opts.targetBranch ? { target_branch: opts.targetBranch } : {}),
          ...(opts.reviewToken ? { review_token: opts.reviewToken } : {}),
        }),
      },
    );
    return {
      proposal: r.proposal ? proposalFromRow(r.proposal) : null,
      proposalId: r.proposal_id,
      targetBranch: r.target_branch,
      movedOps: r.moved_ops ?? 0,
    };
  },

  /** Accept: land the proposal on its target. A refusal (a conflict, the
   *  target moved, a pending action that cannot be done) comes back as
   *  `LaneBlocked` / the uniform refusal, for the one notice. */
  acceptProposal(islandId: string, proposalId: string, opts: { reason?: string; choices?: LaneChoice[]; expectedBase?: string | null; reviewToken?: string | null } = {}): Promise<{ accepted: boolean; commit_id?: string | null }> {
    return request(`${islandId}/proposals/${encodeURIComponent(proposalId)}/accept`, {
      method: "POST",
      body: JSON.stringify({
        choices: opts.choices ?? [],
        ...(opts.expectedBase ? { expected_base_commit_id: opts.expectedBase } : {}),
        ...(opts.reviewToken ? { review_token: opts.reviewToken } : {}),
        ...(opts.reason ? { reason: opts.reason } : {}),
      }),
    });
  },

  /** Close without landing: `rejected` by a decider, `withdrawn` by its
   *  author (VC-D / VC-core, 2026-10-12: one route, `/discard`, with `as`). */
  closeProposal(islandId: string, proposalId: string, as: "rejected" | "withdrawn", reason?: string): Promise<unknown> {
    return request(`${islandId}/proposals/${encodeURIComponent(proposalId)}/discard`, {
      method: "POST",
      body: JSON.stringify({ as, ...(reason ? { reason } : {}) }),
    });
  },

  async discardOverlay(overlayId: string): Promise<void> {
    await new VersioningOverlay({ id: overlayId } as any).delete();
  },

  /** Read-only historical reconstruction at a commit. */
  async asOf(islandId: string, commitId: string): Promise<AsOfGraph> {
    const body = await request<{ graph: AsOfGraph }>(`${islandId}/as-of/${encodeURIComponent(commitId)}`);
    return body.graph;
  },
};

export type VersioningClient = typeof versioningClient;
